import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SectionHeader from '../../components/SectionHeader';
import FormInput from '../../components/FormInput';
import FormEntry from '../../components/FormEntry';
import { useUser } from 'hooks/useUser';
import { useTeams } from '../../hooks/useTeam';
import { useUsers, useUpdateUser, useResetUserPassword } from '../../hooks/useUsers';
import { createTeam, updateTeam } from '../../api/teams';
import { User } from '../../types/user';
import toast from 'react-hot-toast';
import PencilIcon from '../../icons/PencilIcon';
import { updateUser, createUser } from 'api/users';
import { HiChevronRight } from 'react-icons/hi2';
import Button from '../../components/Button';
import FormCard from '../../components/FormCard';
import ChainModelsSection from './ChainModelsSection';
import { clearSingletonSessionsCache } from '../../hooks/useSingletonSessions';
import {
  FEATURE_DEFAULTS,
  FEATURE_LABELS,
  resolvePrivileges,
  TeamPrivileges,
} from '../../constants/teamPrivileges';

interface TeamsAdminFormData {
  teamId: string;
}

type EditableUserFields = Pick<User, 'user_name'>;

export default function TeamsAdmin(): ReactElement {
  const queryClient = useQueryClient();
  const [userProfile] = useUser();
  const { data: teams, isLoading: teamsLoading } = useTeams();
  const { data: users, isLoading: usersLoading } = useUsers();
  const updateUserMutation = useUpdateUser();
  const resetPasswordMutation = useResetUserPassword();

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const [editFormValues, setEditFormValues] = useState<EditableUserFields>({
    user_name: '',
  });

  // Add Team state
  const [newTeamName, setNewTeamName] = useState('');
  const [isAddTeamExpanded, setIsAddTeamExpanded] = useState(false);

  // Team Settings state
  const [isTeamSettingsExpanded, setIsTeamSettingsExpanded] = useState(false);
  const [xwRefAngleInput, setXwRefAngleInput] = useState<string>('');

  // Team Privileges state — has its own team picker so an admin can grant
  // features to any team without switching into it.
  const [isPrivilegesExpanded, setIsPrivilegesExpanded] = useState(false);
  const [privilegesTeamId, setPrivilegesTeamId] = useState<string>('');
  const [privilegesDraft, setPrivilegesDraft] = useState<TeamPrivileges>({ ...FEATURE_DEFAULTS });

  // Add User state
  const [newUser, setNewUser] = useState({
    teamId: '',
    userName: '',
    emailAddress: '',
  });
  const [isAddUserExpanded, setIsAddUserExpanded] = useState(false);

  const teamNameMap = useMemo(() => {
    if (!teams || !Array.isArray(teams)) {
      return new Map<string, string>();
    }
    return new Map<string, string>((teams as { id: string; name: string }[]).map((team) => [team.id, team.name]));
  }, [teams]);

  const sortedUsers = useMemo(() => {
    if (!users) {
      return [] as User[];
    }

    return [...users].sort((a, b) => {
      const teamA = teamNameMap.get(a.team_id) || '';
      const teamB = teamNameMap.get(b.team_id) || '';

      if (!teamA && teamB) {
        return 1;
      }

      if (teamA && !teamB) {
        return -1;
      }

      const teamComparison = teamA.localeCompare(teamB, undefined, { sensitivity: 'base' });
      if (teamComparison !== 0) {
        return teamComparison;
      }

      return (a.user_name || '').localeCompare(b.user_name || '', undefined, { sensitivity: 'base' });
    });
  }, [users, teamNameMap]);

  const formMethods = useForm<TeamsAdminFormData>({
    defaultValues: {
      teamId: userProfile.team_id || '',
    },
  });

  // Create mutation for updating current user's team
  const updateTeamMutation = useMutation({
    mutationFn: (data: any) => updateUser(userProfile.id, data),
    onSuccess: () => {
      // Every data cache except the profile is team-scoped but keyed without a
      // team id, so a swap must drop them outright — invalidation alone would
      // keep serving the previous team's data while the refetch is in flight.
      clearSingletonSessionsCache();
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'profile' });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Team updated successfully');
    },
    onError: (error) => {
      console.error('Error updating user:', error);
      toast.error('Failed to update team');
    },
  });

  // Update form when userProfile changes
  useEffect(() => {
    if (userProfile.team_id) {
      formMethods.setValue('teamId', userProfile.team_id);
    }
  }, [userProfile.team_id, formMethods]);

  // Watch for changes in teamId field and update user
  useEffect(() => {
    const subscription = formMethods.watch((value, { name }) => {
      if (name === 'teamId' && value.teamId && value.teamId !== userProfile.team_id) {
        const updatedUser = {
          ...userProfile,
          team_id: value.teamId,
        };
        updateTeamMutation.mutate(updatedUser);
      }
    });

    return () => subscription.unsubscribe();
  }, [formMethods, userProfile, updateTeamMutation]);

  const startEditUser = (user: User) => {
    setEditingUserId(user.id);
    setEditFormValues({
      user_name: user.user_name ?? '',
    });
  };

  const handleEditChange = (field: keyof EditableUserFields, value: string) => {
    setEditFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditFormValues({
      user_name: '',
    });
  };

  const handleSaveEdit = (user: User) => {
    updateUserMutation.mutate(
      {
        userId: user.id,
        user: {
          user_name: editFormValues.user_name || user.user_name,
        },
      },
      {
        onSuccess: () => {
          setEditingUserId(null);
          setEditFormValues({
            user_name: '',
          });
        },
      },
    );
  };

  const handleResetPassword = (user: User) => {
    setResettingUserId(user.id);
    resetPasswordMutation.mutate(user.id, {
      onSettled: () => {
        setResettingUserId(null);
      },
    });
  };

  // Add Team mutation
  const addTeamMutation = useMutation({
    mutationFn: (data: { name: string }) => createTeam(data),
    onSuccess: () => {
      toast.success('Team added successfully');
      setNewTeamName('');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (error) => {
      console.error('Error adding team:', error);
      toast.error('Failed to add team');
    },
  });

  const currentTeam = useMemo(() => {
    if (!teams || !Array.isArray(teams) || !userProfile.team_id) return null;
    return (teams as any[]).find((t) => t.id === userProfile.team_id) ?? null;
  }, [teams, userProfile.team_id]);

  useEffect(() => {
    const v = currentTeam?.xw_ref_angle;
    setXwRefAngleInput(v == null ? '' : String(v));
  }, [currentTeam?.xw_ref_angle]);

  const updateTeamSettingsMutation = useMutation({
    mutationFn: (data: { id: string; xw_ref_angle: number | null }) =>
      updateTeam(data.id, { xw_ref_angle: data.xw_ref_angle }),
    onSuccess: () => {
      toast.success('Team settings updated');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (error) => {
      console.error('Error updating team:', error);
      toast.error('Failed to update team settings');
    },
  });

  const handleSaveTeamSettings = () => {
    if (!currentTeam?.id) return;
    const trimmed = xwRefAngleInput.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0 || parsed > 15)) {
      toast.error('Reference yaw angle must be between 0 and 15 degrees');
      return;
    }
    updateTeamSettingsMutation.mutate({ id: currentTeam.id, xw_ref_angle: parsed });
  };

  // Team Privileges — picker defaults to the caller's own team; the checkbox
  // draft reseeds from the stored overrides whenever the picked team changes.
  useEffect(() => {
    if (!privilegesTeamId && userProfile.team_id) {
      setPrivilegesTeamId(userProfile.team_id);
    }
  }, [privilegesTeamId, userProfile.team_id]);

  const privilegesTeam = useMemo(() => {
    if (!teams || !Array.isArray(teams) || !privilegesTeamId) return null;
    return (teams as any[]).find((t) => t.id === privilegesTeamId) ?? null;
  }, [teams, privilegesTeamId]);

  useEffect(() => {
    setPrivilegesDraft(resolvePrivileges(privilegesTeam?.privileges));
  }, [privilegesTeam]);

  const updateTeamPrivilegesMutation = useMutation({
    mutationFn: (data: { id: string; privileges: TeamPrivileges }) =>
      updateTeam(data.id, { privileges: data.privileges }),
    onSuccess: () => {
      toast.success('Team privileges updated');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      // Editing your own team must re-gate the UI without a re-login.
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      console.error('Error updating team privileges:', error);
      toast.error('Failed to update team privileges');
    },
  });

  const handleSavePrivileges = () => {
    if (!privilegesTeam?.id) return;
    updateTeamPrivilegesMutation.mutate({ id: privilegesTeam.id, privileges: privilegesDraft });
  };

  // Add User mutation
  const addUserMutation = useMutation({
    mutationFn: (data: { team_id: string; user_name: string; email_address: string; user_role: number }) =>
      createUser(data),
    onSuccess: () => {
      toast.success('User added successfully');
      setNewUser({ teamId: '', userName: '', emailAddress: '' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      console.error('Error adding user:', error);
      toast.error('Failed to add user');
    },
  });

  const handleAddTeam = () => {
    if (newTeamName.trim()) {
      addTeamMutation.mutate({ name: newTeamName.trim() });
    }
  };

  const handleAddUser = () => {
    if (newUser.teamId && newUser.userName.trim() && newUser.emailAddress.trim()) {
      addUserMutation.mutate({
        team_id: newUser.teamId,
        user_name: newUser.userName.trim(),
        email_address: newUser.emailAddress.trim(),
        user_role: 5000,
      });
    }
  };

  const isAddTeamValid = newTeamName.trim().length > 0;
  const isAddUserValid =
    newUser.teamId.length > 0 && newUser.userName.trim().length > 0 && newUser.emailAddress.trim().length > 0;

  return (
    <FormProvider {...formMethods}>
      <SectionHeader title="Teams" />

      {/* Team Selection Section */}
      <FormCard>
        <div className="cr-p-3">
          <FormEntry label="Select Team">
            <FormInput fieldName="teamId" options={teams} optionLabel="name" customClass="cr-w-full" />
            {teamsLoading && <p className="cr-mt-1 cr-text-sm cr-text-gray-500">Loading teams...</p>}
            {updateTeamMutation.isPending && <p className="cr-mt-1 cr-text-sm cr-text-blue-500">Updating team...</p>}
          </FormEntry>
        </div>
      </FormCard>

      {/* Add Team Section */}
      <FormCard>
        <button
          className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
          onClick={() => setIsAddTeamExpanded(!isAddTeamExpanded)}
        >
          <h3 className="cr-text-lg cr-font-semibold">Add Team</h3>
          <HiChevronRight
            className={`cr-transition-transform cr-duration-200 ${isAddTeamExpanded ? 'cr-rotate-90' : ''}`}
            size={20}
          />
        </button>
        {isAddTeamExpanded && (
          <div className="cr-border-t cr-border-gray-200 cr-p-3">
            <div className="cr-flex cr-flex-col cr-gap-3">
              <label className="cr-flex cr-flex-col">
                <span className="cr-text-xs cr-font-medium cr-text-gray-500">Name</span>
                <input
                  className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Team name"
                />
              </label>
              <Button
                onClick={handleAddTeam}
                disabled={!isAddTeamValid || addTeamMutation.isPending}
                variant="secondary"
                className="cr-w-fit"
              >
                {addTeamMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </FormCard>

      {/* Team Settings Section */}
      <FormCard>
        <button
          className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
          onClick={() => setIsTeamSettingsExpanded(!isTeamSettingsExpanded)}
        >
          <h3 className="cr-text-lg cr-font-semibold">Team Settings</h3>
          <HiChevronRight
            className={`cr-transition-transform cr-duration-200 ${isTeamSettingsExpanded ? 'cr-rotate-90' : ''}`}
            size={20}
          />
        </button>
        {isTeamSettingsExpanded && (
          <div className="cr-border-t cr-border-gray-200 cr-p-3">
            <div className="cr-flex cr-flex-col cr-gap-3">
              <label className="cr-flex cr-flex-col">
                <span className="cr-text-xs cr-font-medium cr-text-gray-500">
                  CdA Normalisation Reference Yaw Angle (°)
                </span>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={15}
                  className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none md:cr-w-[240px]"
                  value={xwRefAngleInput}
                  onChange={(e) => setXwRefAngleInput(e.target.value)}
                  placeholder="2.0 (default)"
                  disabled={!currentTeam}
                />
                <span className="cr-mt-1 cr-text-xs cr-text-gray-400">
                  Angle CdA values are normalised to. Leave blank to use the application default (2°).
                </span>
              </label>
              <Button
                onClick={handleSaveTeamSettings}
                disabled={!currentTeam || updateTeamSettingsMutation.isPending}
                variant="secondary"
                className="cr-w-fit"
              >
                {updateTeamSettingsMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </FormCard>

      {/* Team Privileges Section — per-team licensed features */}
      {userProfile.user_role >= 8000 && (
        <FormCard>
          <button
            className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
            onClick={() => setIsPrivilegesExpanded(!isPrivilegesExpanded)}
          >
            <h3 className="cr-text-lg cr-font-semibold">Team Privileges</h3>
            <HiChevronRight
              className={`cr-transition-transform cr-duration-200 ${isPrivilegesExpanded ? 'cr-rotate-90' : ''}`}
              size={20}
            />
          </button>
          {isPrivilegesExpanded && (
            <div className="cr-border-t cr-border-gray-200 cr-p-3">
              <div className="cr-flex cr-flex-col cr-gap-3">
                <label className="cr-flex cr-flex-col">
                  <span className="cr-text-xs cr-font-medium cr-text-gray-500">Team</span>
                  <select
                    className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none md:cr-w-[240px]"
                    value={privilegesTeamId}
                    onChange={(e) => setPrivilegesTeamId(e.target.value)}
                  >
                    {teams?.map((team: any) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </label>
                {Object.keys(FEATURE_LABELS).map((featureKey) => (
                  <label key={featureKey} className="cr-flex cr-items-center cr-gap-2 cr-text-sm">
                    <input
                      type="checkbox"
                      checked={privilegesDraft[featureKey] ?? false}
                      onChange={(e) =>
                        setPrivilegesDraft({ ...privilegesDraft, [featureKey]: e.target.checked })
                      }
                    />
                    <span>{FEATURE_LABELS[featureKey]}</span>
                  </label>
                ))}
                <Button
                  onClick={handleSavePrivileges}
                  disabled={!privilegesTeam || updateTeamPrivilegesMutation.isPending}
                  variant="secondary"
                  className="cr-w-fit"
                >
                  {updateTeamPrivilegesMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </FormCard>
      )}

      {/* Add User Section */}
      <FormCard>
        <button
          className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
          onClick={() => setIsAddUserExpanded(!isAddUserExpanded)}
        >
          <h3 className="cr-text-lg cr-font-semibold">Add User</h3>
          <HiChevronRight
            className={`cr-transition-transform cr-duration-200 ${isAddUserExpanded ? 'cr-rotate-90' : ''}`}
            size={20}
          />
        </button>
        {isAddUserExpanded && (
          <div className="cr-border-t cr-border-gray-200 cr-p-3">
            <div className="cr-flex cr-flex-col cr-gap-3">
              <label className="cr-flex cr-flex-col">
                <span className="cr-text-xs cr-font-medium cr-text-gray-500">Select Team</span>
                <select
                  className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  value={newUser.teamId}
                  onChange={(e) => setNewUser({ ...newUser, teamId: e.target.value })}
                >
                  <option value="">Select a team...</option>
                  {teams?.map((team: any) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cr-flex cr-flex-col">
                <span className="cr-text-xs cr-font-medium cr-text-gray-500">User Name</span>
                <input
                  className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  value={newUser.userName}
                  onChange={(e) => setNewUser({ ...newUser, userName: e.target.value })}
                  placeholder="User name"
                />
              </label>
              <label className="cr-flex cr-flex-col">
                <span className="cr-text-xs cr-font-medium cr-text-gray-500">Email Address</span>
                <input
                  type="email"
                  className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                  value={newUser.emailAddress}
                  onChange={(e) => setNewUser({ ...newUser, emailAddress: e.target.value })}
                  placeholder="email@example.com"
                />
              </label>
              <Button
                onClick={handleAddUser}
                disabled={!isAddUserValid || addUserMutation.isPending}
                variant="secondary"
                className="cr-w-fit"
              >
                {addUserMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </FormCard>

      {/* Chain Models Section */}
      <ChainModelsSection
        teamId={userProfile.team_id || null}
        teamName={
          teamNameMap.get(userProfile.team_id) || 'Unknown'
        }
      />

      {/* User Management Section */}
      <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
        <div className="cr-p-3">
          <h3 className="cr-mb-4 cr-text-lg cr-font-semibold">User Management</h3>

          {usersLoading ? (
            <p className="cr-text-sm cr-text-gray-500">Loading users...</p>
          ) : (
            <div className="cr-space-y-2">
              {sortedUsers.map((user) => {
                const isEditing = editingUserId === user.id;
                const teamName = teamNameMap.get(user.team_id) || 'Unknown';
                const isResetting = resetPasswordMutation.isPending && resettingUserId === user.id;

                return (
                  <div
                    key={user.id}
                    className="cr-flex cr-flex-col cr-gap-4 cr-rounded-md cr-border cr-border-gray-200 cr-p-3 hover:cr-bg-gray-50 md:cr-flex-row md:cr-items-start md:cr-justify-between"
                  >
                    <div className="cr-flex-1">
                      {isEditing ? (
                        <div className="cr-flex cr-flex-col cr-gap-3 md:cr-max-w-[520px]">
                          <label className="cr-flex cr-flex-col">
                            <span className="cr-text-xs cr-font-medium cr-text-gray-500">Name</span>
                            <input
                              className="cr-mt-1 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none md:cr-w-[480px]"
                              value={editFormValues.user_name}
                              onChange={(event) => handleEditChange('user_name', event.target.value)}
                              placeholder="User name"
                            />
                          </label>
                          <div className="cr-text-sm cr-text-gray-500">
                            <span className="cr-font-medium">Email:</span> {user.email_address}
                          </div>
                          <div className="cr-text-sm cr-text-gray-500">
                            <span className="cr-font-medium">Team:</span> {teamName}
                          </div>
                          <div className="cr-mt-2 cr-flex cr-flex-wrap cr-gap-2">
                            <button
                              className="cr-rounded-md cr-bg-blue-600 cr-px-3 cr-py-1 cr-text-sm cr-font-medium cr-text-white hover:cr-bg-blue-700 disabled:cr-cursor-not-allowed disabled:cr-bg-blue-400"
                              onClick={() => handleSaveEdit(user)}
                              disabled={updateUserMutation.isPending}
                            >
                              Save
                            </button>
                            <button
                              className="cr-rounded-md cr-bg-gray-200 cr-px-3 cr-py-1 cr-text-sm cr-font-medium cr-text-gray-700 hover:cr-bg-gray-300 disabled:cr-cursor-not-allowed disabled:cr-bg-gray-200"
                              onClick={handleCancelEdit}
                              disabled={updateUserMutation.isPending || isResetting}
                            >
                              Cancel
                            </button>
                            <button
                              className="cr-rounded-md cr-border cr-border-blue-600 cr-px-3 cr-py-1 cr-text-sm cr-font-medium cr-text-blue-600 hover:cr-bg-blue-50 disabled:cr-cursor-not-allowed disabled:cr-border-blue-300 disabled:cr-text-blue-300"
                              onClick={() => handleResetPassword(user)}
                              disabled={isResetting}
                            >
                              {isResetting ? 'Resetting...' : 'Reset Password'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="cr-flex cr-flex-col">
                          <span className="cr-font-medium">{user.user_name}</span>
                          <span className="cr-text-sm cr-text-gray-500">{user.email_address}</span>
                          <span className="cr-text-xs cr-text-gray-400">Team: {teamName}</span>
                        </div>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="cr-mt-2 cr-flex cr-justify-end md:cr-ml-4 md:cr-mt-0">
                        <button
                          className="cr-flex cr-items-center cr-justify-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-p-2 cr-text-gray-600 hover:cr-bg-gray-100"
                          onClick={() => startEditUser(user)}
                          aria-label={`Edit ${user.user_name}`}
                        >
                          <PencilIcon size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
}
